import { useState, useEffect } from 'react';
import { X, Printer, FileText, Loader2, AlertCircle } from 'lucide-react';
import { getRegistryPayoutBreakdown } from '../lib/admin';
import type { InvoiceData } from '../types/admin';

interface Props {
  registryId: string;
  registryTitle: string;
  onClose: () => void;
}

export default function InvoiceModal({ registryId, registryTitle, onClose }: Props) {
  const [data, setData] = useState<InvoiceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');
        const breakdown = await getRegistryPayoutBreakdown(registryId);
        setData(breakdown);
      } catch (err) {
        console.error('Error loading invoice data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load invoice data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [registryId]);

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatAmount = (amount: number) => {
    return `LKR ${Number(amount).toFixed(2)}`;
  };

  const feeModelLabel = (responsibility: string) => {
    if (responsibility === 'GUEST') return 'Guest Pays';
    if (responsibility === 'COUPLE') return 'Couple Absorbs';
    if (responsibility === 'SPLIT') return 'Split 50/50';
    return responsibility || 'N/A';
  };

  return (
    <div className="fixed inset-0 bg-black/20 flex items-center justify-center p-4 z-50 print:bg-white print:p-0 print:static print:block">
      <div
        className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto print:max-h-none print:rounded-none print:shadow-none"
        id="invoice-print-area"
      >
        {/* Header - hidden in print, shown on screen */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 print:hidden">
          <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2 font-poppins">
            <FileText className="w-5 h-5 text-pastel-blue-600" />
            Payout Invoice — {registryTitle}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              disabled={loading || !!error}
              className="flex items-center gap-2 px-4 py-2 bg-pastel-blue-600 text-white rounded-md hover:bg-pastel-blue-700 disabled:opacity-50 text-sm font-poppins"
            >
              <Printer className="w-4 h-4" />
              <span>Print / Save PDF</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/60 rounded-full transition-colors text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-24 print:hidden">
            <Loader2 className="w-8 h-8 animate-spin text-pastel-blue-600" />
            <span className="ml-3 text-gray-600 font-poppins">Loading invoice data...</span>
          </div>
        )}

        {error && (
          <div className="m-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded-md flex items-start space-x-2 print:hidden">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span className="text-sm font-poppins">{error}</span>
          </div>
        )}

        {data && !loading && !error && (
          <div className="p-8 print:p-6">
            {/* Invoice header */}
            <div className="flex justify-between items-start mb-8 pb-6 border-b-2 border-gray-300">
              <div>
                <h1 className="text-3xl font-bold text-gray-800 mb-1 font-poppins">Giftyfy</h1>
                <p className="text-sm text-gray-500 font-poppins">Gift Registry Platform</p>
                <p className="text-xs text-gray-400 mt-2 font-poppins">support@giftyfy.com</p>
              </div>
              <div className="text-right">
                <h2 className="text-2xl font-bold text-gray-800 mb-1 font-poppins">PAYOUT INVOICE</h2>
                <p className="text-sm text-gray-500 font-poppins">Invoice Date: {formatDate(new Date().toISOString())}</p>
                <p className="text-sm text-gray-500 mt-1 font-poppins">Registry ID: {data.registry_id.slice(0, 8)}</p>
              </div>
            </div>

            {/* Bill To section */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 font-poppins">Bill To</h3>
                <p className="text-lg font-medium text-gray-800 font-poppins">{data.owner_name}</p>
                <p className="text-sm text-gray-600 font-poppins">{data.owner_email}</p>
                {data.contact_number && (
                  <p className="text-sm text-gray-600 font-poppins">{data.contact_number}</p>
                )}
                {data.delivery_address && (
                  <p className="text-sm text-gray-600 mt-1 font-poppins">{data.delivery_address}</p>
                )}
              </div>
              <div className="text-right">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 font-poppins">Registry</h3>
                <p className="text-lg font-medium text-gray-800 font-poppins">{data.title}</p>
                <p className="text-sm text-gray-600 mt-1 font-poppins">Fee Model: {feeModelLabel(data.fee_responsibility)}</p>
                <p className="text-sm text-gray-600 font-poppins">
                  Platform Fee: {data.platform_fee_percentage.toFixed(2)}%
                </p>
                <p className="text-sm text-gray-600 font-poppins">
                  Gateway Fee: {data.gateway_fee_percentage.toFixed(2)}%
                </p>
              </div>
            </div>

            {/* Summary section */}
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 font-poppins">Financial Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 font-poppins">Total Captured Transactions</span>
                  <span className="font-medium text-gray-800 font-poppins">{data.transaction_count}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 font-poppins">Gross Gift Amount (before fees)</span>
                  <span className="font-medium text-gray-800 font-poppins">{formatAmount(data.gross_gift_amount)}</span>
                </div>
                <div className="flex justify-between text-sm pl-4">
                  <span className="text-gray-500 font-poppins">Platform Fee ({data.platform_fee_percentage.toFixed(2)}%)</span>
                  <span className="text-red-600 font-poppins">- {formatAmount(data.platform_fee_total)}</span>
                </div>
                <div className="flex justify-between text-sm pl-4">
                  <span className="text-gray-500 font-poppins">Gateway Fee ({data.gateway_fee_percentage.toFixed(2)}%)</span>
                  <span className="text-red-600 font-poppins">- {formatAmount(data.gateway_fee_total)}</span>
                </div>
                <div className="flex justify-between text-sm pl-4 border-t border-gray-200 pt-2">
                  <span className="text-gray-700 font-medium font-poppins">Total Fees Collected</span>
                  <span className="text-red-600 font-medium font-poppins">- {formatAmount(data.total_fees)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 font-poppins">Fees Paid by Guests (on top of gifts)</span>
                  <span className="text-green-600 font-poppins">+ {formatAmount(data.guest_fee_share_total)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 font-poppins">Fees Deducted from Couple's Gifts</span>
                  <span className="text-red-600 font-poppins">- {formatAmount(data.couple_fee_share_total)}</span>
                </div>
                <div className="flex justify-between text-base border-t-2 border-gray-300 pt-3 mt-2">
                  <span className="font-bold text-gray-800 font-poppins">Net Payable to Client</span>
                  <span className="font-bold text-green-700 text-lg font-poppins">{formatAmount(data.net_payable)}</span>
                </div>
              </div>
            </div>

            {/* Withdrawal history */}
            <div className="bg-pastel-blue-50 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 font-poppins">Withdrawal Status</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 font-poppins">Net Payable to Client</span>
                  <span className="font-medium text-gray-800 font-poppins">{formatAmount(data.net_payable)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 font-poppins">Already Withdrawn</span>
                  <span className="text-orange-600 font-poppins">- {formatAmount(data.total_withdrawn)}</span>
                </div>
                <div className="flex justify-between text-base border-t-2 border-gray-300 pt-3 mt-2">
                  <span className="font-bold text-gray-800 font-poppins">Remaining Balance Owed</span>
                  <span className="font-bold text-pastel-blue-700 text-lg font-poppins">{formatAmount(data.remaining_balance)}</span>
                </div>
              </div>
            </div>

            {/* Transaction table */}
            {data.transactions.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 font-poppins">Transaction Details</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm border border-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 font-medium text-gray-600 border-b font-poppins">Date</th>
                        <th className="px-3 py-2 font-medium text-gray-600 border-b font-poppins">Guest</th>
                        <th className="px-3 py-2 font-medium text-gray-600 border-b text-right font-poppins">Gift Amount</th>
                        <th className="px-3 py-2 font-medium text-gray-600 border-b text-right font-poppins">Platform Fee</th>
                        <th className="px-3 py-2 font-medium text-gray-600 border-b text-right font-poppins">Gateway Fee</th>
                        <th className="px-3 py-2 font-medium text-gray-600 border-b text-right font-poppins">Couple Fee Share</th>
                        <th className="px-3 py-2 font-medium text-gray-600 border-b text-right font-poppins">Net to Couple</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.transactions.map((tx) => (
                        <tr key={tx.order_id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-600 font-poppins">{formatDate(tx.captured_at || tx.created_at)}</td>
                          <td className="px-3 py-2 text-gray-800 font-poppins">
                            {tx.payer_first_name} {tx.payer_last_name}
                            {tx.is_group_gift && (
                              <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-poppins">
                                Group Gift
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-800 font-poppins">{formatAmount(tx.original_amount)}</td>
                          <td className="px-3 py-2 text-right text-red-600 font-poppins">{formatAmount(tx.platform_fee)}</td>
                          <td className="px-3 py-2 text-right text-red-600 font-poppins">{formatAmount(tx.gateway_fee)}</td>
                          <td className="px-3 py-2 text-right text-red-600 font-poppins">{formatAmount(tx.couple_fee_share)}</td>
                          <td className="px-3 py-2 text-right font-medium text-green-700 font-poppins">
                            {formatAmount(tx.original_amount - tx.couple_fee_share)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-100 font-medium">
                      <tr>
                        <td className="px-3 py-2 text-gray-700 font-poppins" colSpan={2}>Totals</td>
                        <td className="px-3 py-2 text-right text-gray-800 font-poppins">{formatAmount(data.gross_gift_amount)}</td>
                        <td className="px-3 py-2 text-right text-red-600 font-poppins">{formatAmount(data.platform_fee_total)}</td>
                        <td className="px-3 py-2 text-right text-red-600 font-poppins">{formatAmount(data.gateway_fee_total)}</td>
                        <td className="px-3 py-2 text-right text-red-600 font-poppins">{formatAmount(data.couple_fee_share_total)}</td>
                        <td className="px-3 py-2 text-right text-green-700 font-poppins">{formatAmount(data.net_payable)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Footer notes */}
            <div className="border-t border-gray-200 pt-6 mt-8">
              <div className="text-xs text-gray-500 space-y-1 font-poppins">
                <p>
                  <span className="font-medium text-gray-600">Fee Model:</span>{' '}
                  {feeModelLabel(data.fee_responsibility)} —{' '}
                  {data.fee_responsibility === 'GUEST' && 'All fees are added on top of gift amounts. The couple receives the full gift amount.'}
                  {data.fee_responsibility === 'COUPLE' && 'All fees are deducted from the gift amount. The guest pays only the gift amount.'}
                  {data.fee_responsibility === 'SPLIT' && 'Half the fee is added on top (paid by guest), half is deducted from the gift (paid by couple).'}
                </p>
                <p>
                  <span className="font-medium text-gray-600">Platform Fee</span> is the commission collected by Giftyfy.
                  <span className="font-medium text-gray-600 ml-2">Gateway Fee</span> is the PayHere payment processing cost.
                </p>
                <p>
                  <span className="font-medium text-gray-600">Net Payable</span> = Gross Gift Amount - Fees Deducted from Couple's Gifts.
                  This is the amount owed to the client after all platform and gateway fees are accounted for.
                </p>
                <p>
                  <span className="font-medium text-gray-600">Remaining Balance</span> = Net Payable - Already Withdrawn.
                  This is the outstanding amount to be transferred to the client.
                </p>
                <p className="pt-2 text-gray-400">
                  This invoice was generated automatically by the Giftyfy admin system.
                  Please verify all amounts before processing payment.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}